//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';

import { SamplingCatalogProductsResponse } from '@apis/campaign-requests/sampling/products/samplingProductsApiV2Model';
import {
  searchBvCatalogProducts,
  searchCuralateCatalogProducts,
} from '@apis/universal-image-actions/universalImageActionsApi';
import {
  CuralateCatalogProduct,
  CuralateCatalogProducts,
} from '@apis/universal-image-actions/universalImageActionsApiModel';
import ClientId from '@models/ClientId';
import PartialCuid from '@models/PartialCuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { isDefined, isTruthy } from '@utils/filterPredicates';

const mapBVResponseToCuralateProducts = ({
  products,
}: SamplingCatalogProductsResponse): CuralateCatalogProduct[] => {
  if (isDefined(products)) {
    return products.reduce<CuralateCatalogProduct[]>(
      (formattedProducts, product) => {
        if (isDefined(product.styleId) && !!product.productDetailPageUrl) {
          formattedProducts.push({
            description: product.description,
            destinationUrl: product.productDetailPageUrl,
            externalId: product.samplingProductId,
            images: [
              {
                fileId: product.fileId,
                imageUrl: product.imageUrl,
                sourceImageUrl: product.imageUrl,
              },
            ],
            name: product.name,
            productStyleId: product.styleId,
            styleId: product.styleId,
          });
        }

        return formattedProducts;
      },
      [],
    );
  }
  return [];
};

export interface ProductsParams {
  catalogClientId?: ClientId;
  getCatalogSuggestions: () => CuralateCatalogProduct[];
  hasCatalog: boolean;
  isBvCampaignTagged?: boolean;
  isEnabled: boolean;
  isShareV2Vendor?: boolean;
  limit: number;
  offset: number;
  itemId: PartialCuid | undefined;
  productSuggestionBoxId: string;
  search: string;
}

export const useProductsQuery = ({
  catalogClientId,
  getCatalogSuggestions,
  hasCatalog,
  isBvCampaignTagged = false,
  isEnabled,
  isShareV2Vendor,
  itemId,
  limit,
  offset,
  productSuggestionBoxId,
  search,
}: ProductsParams) =>
  useQuery<CuralateCatalogProducts>({
    enabled: isEnabled && hasCatalog && isDefined(itemId), // TODO: PD-282907 - Remove isEnabled when the UIA Products Api Port feature flag is removed
    queryKey: [
      QueryKeyConstants.UniversalImageActions,
      QueryKeyConstants.UniversalImageActionsProducts,
      QueryKeyConstants.UniversalImageActionsProductsList,
      {
        isBvCampaignTagged,
        itemId,
        limit,
        offset,
        productSuggestionBoxId,
        search,
      },
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(itemId)) {
        throw new Error('ItemId is required to fetch products');
      }

      // If we have search text present i.e. keywords, we perform a search for the CuralateCatalogProducts,
      // otherwise we return the catalog suggestions
      if (isTruthy(search)) {
        // If client is a shareV2 vendor, we'll use web_gateway even for BV campaigns
        if (isBvCampaignTagged && !isShareV2Vendor) {
          const res = await searchBvCatalogProducts(
            {
              searchText: search,
            },
            { signal },
          );
          return {
            products: mapBVResponseToCuralateProducts(res),
            totalCount: res.total,
          };
        } else {
          return await searchCuralateCatalogProducts(
            { catalogClientId, itemId, limit, offset, search },
            { signal },
          );
        }
      } else {
        const products = getCatalogSuggestions();
        return {
          isCatalogSuggestions: true,
          products,
          totalCount: products.length,
        };
      }
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
